// What an EMPTY cell reads as — the two places fast-formula-parser gets it wrong,
// plus a deliberate departure from Excel for a cell that only LOOKS blank.
//
// A blank cell reaches the library as `{value: null}`, and two of its paths
// never expected a null inside the wrapper:
//
//   LEN(A1)   15   — `accept` keeps the wrapper when the value is null and
//                    stringifies it: "[object Object]". TRIM, LEFT and every
//                    other text function go the same way.
//   A1=""     FALSE — the comparison turns a blank into 0 before it looks at
//                    the other side, and 0 is not "".
//
// Excel's rule is that a blank takes the type of whatever it meets: "" beside
// text, 0 beside a number, FALSE beside a boolean. Both fixes apply that rule
// and touch nothing else — a value that is not blank takes the library's own
// path untouched.
//
// A THIRD case, deliberately not what Excel does: `=B53-B54` is `#VALUE!`
// when B54 holds `=IF($B54="","",…)` — a common "leave the row blank until
// there's a value" pattern — because B54 then evaluates to the TEXT "",
// not a blank cell, and Excel genuinely does treat those differently
// (`=""-1` is `#VALUE!` there too). A sheet author can't tell those apart by
// looking at the grid — both show an empty cell — so for `+ - * / ^` only,
// an empty-string operand is treated the same as a blank one: 0.

import type FormulaParser from 'fast-formula-parser';
import { FormulaHelpers, Types } from 'fast-formula-parser';

type Accept = (param: unknown, type?: unknown, ...rest: unknown[]) => unknown;

const COMPARE = new Set(['=', '<>', '<', '>', '<=', '>=']);
const MATH = new Set(['+', '-', '*', '/', '^']);

/** A resolved single-cell argument whose cell is empty. */
function isBlankArg(param: unknown): boolean {
  if (param === null || typeof param !== 'object' || Array.isArray(param)) return false;
  const p = param as { value?: unknown; isArray?: boolean; omitted?: boolean };
  return 'value' in p && p.value == null && !p.isArray && !p.omitted;
}

let accepting = false;

/**
 * `FormulaHelpers` is the library's one shared helper object, so this runs
 * once per module, not per parser.
 */
function patchAccept(): void {
  if (accepting) return;
  accepting = true;
  const helpers = FormulaHelpers as unknown as { accept: Accept };
  const original = helpers.accept.bind(helpers);
  helpers.accept = (param, type, ...rest) => {
    if (isBlankArg(param)) {
      const t = Array.isArray(type) ? type[0] : type;
      if (t === Types.STRING) return '';
      if (t === Types.NUMBER || t === Types.NUMBER_NO_BOOLEAN) return 0;
      if (t === Types.BOOLEAN) return false;
    }
    return original(param, type, ...rest);
  };
}

/** The blank a comparison should see, given what is on the other side. */
function blankBeside(other: unknown): unknown {
  if (typeof other === 'string') return '';
  if (typeof other === 'boolean') return false;
  return 0;
}

interface InfixSide {
  val: unknown;
  isArray: boolean;
}

/** Fix both paths on this parser. Idempotent for the shared one. */
export function patchBlankCells(parser: FormulaParser): void {
  patchAccept();
  const utils = (parser as unknown as { utils: { _applyInfix: (a: InfixSide, op: string, b: InfixSide) => unknown } })
    .utils;
  const original = utils._applyInfix.bind(utils);
  utils._applyInfix = (a, op, b) => {
    if (COMPARE.has(op)) {
      if (a.val == null && !a.isArray && !b.isArray) a = { ...a, val: blankBeside(b.val) };
      else if (b.val == null && !b.isArray && !a.isArray) b = { ...b, val: blankBeside(a.val) };
    } else if (MATH.has(op)) {
      if (a.val === '' && !a.isArray) a = { ...a, val: 0 };
      if (b.val === '' && !b.isArray) b = { ...b, val: 0 };
    }
    return original(a, op, b);
  };
}
